import { useState } from 'react';
import type { ChangeEvent, FC } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import type { HTTPError } from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors';
import { useSubmitReview } from '../../../bff-api/turn-to/useSubmitReview';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { useWriteReviewFields } from '../../forms/useWriteReviewFields';
import { MAX_MEDIA_FILES_NUMBER } from '../const';
import { validateFilesSize } from '../turn-to-utils';
import { PhotoModal } from './PhotoModal';
import { SubmitQuestionModal } from './SubmitQuestionModal';
import { VideoModal } from './VideoModal';
import { WarningModal } from './WarningModal';
import { WriteReviewModal } from './WriteReviewModal';
import type { CreateReviewType } from '../../../bff-api/turn-to/model';
import type { Photo, Video } from '../types';

export interface WriteReviewFlowProps {
    onCloseWriteReview: () => void;
    onOpenWriteReview: () => void;
    isWriteReviewOpen: boolean;
}
export const WriteReviewFlow: FC<WriteReviewFlowProps> = ({
    isWriteReviewOpen,
    onCloseWriteReview,
    onOpenWriteReview,
}) => {
    const intl = useIntl();
    const { product } = usePDP();
    const postReview = useSubmitReview();

    const [files, setFiles] = useState<FileList | null>(null);
    const [media, setMedia] = useState<(Photo | Video)[]>([]);
    const [isFilesLimitExceeded, setIsFileLimitExceeded] = useState<boolean>(false);

    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            rating: 0,
            review: '',
            reviewTitle: '',
            firstName: '',
            lastName: '',
            email: '',
            nickName: '',
            video: '',
            photo: '',
        },
    });

    const {
        formState: { isDirty },
    } = form;

    const fields = useWriteReviewFields(form);

    const {
        onClose: onCloseWarningModal,
        isOpen: isWarningModalOpen,
        onOpen: onOpenWarningModal,
    } = useDisclosure();

    const {
        onOpen: onOpenPhotoModal,
        onClose: onClosePhotoModal,
        isOpen: isPhotoModalOpen,
    } = useDisclosure();

    const {
        onClose: onCloseVideoModal,
        isOpen: isVideoModalOpen,
        onOpen: onOpenVideoModal,
    } = useDisclosure();

    const {
        onClose: onCloseUserDataModal,
        isOpen: isUserDataModalOpen,
        onOpen: onOpenUserDataModal,
    } = useDisclosure();

    const handleCloseFlow = (): void => {
        if (isDirty || media.length !== 0 || form.getValues('rating') !== 0) {
            onCloseWriteReview();
            onCloseUserDataModal();
            onOpenWarningModal();
            return;
        }

        onCloseModals();
    };

    const onReturnToWriteReview = (): void => {
        onCloseWarningModal();
        onOpenWriteReview();
    };

    const performFilesValidation = (photos: Photo[]): boolean => {
        const isFilesSizeValid = validateFilesSize(photos);
        if (!isFilesSizeValid && !isFilesLimitExceeded) {
            setIsFileLimitExceeded(true);
            return false;
        }

        return true;
    };

    const onSubmitPhotoModal = (submittedPhotos: Photo[]): void => {
        const isFilesValidationPassed = performFilesValidation(submittedPhotos);
        if (!isFilesValidationPassed) {
            return;
        }

        if (isFilesValidationPassed && isFilesLimitExceeded) {
            setIsFileLimitExceeded(false);
        }

        setMedia((prevMedia) => [...prevMedia, ...submittedPhotos]);
        setFiles(null);
    };

    const returnToWriteReviewModal = (closeCallback: () => void): void => {
        closeCallback();
        onOpenWriteReview();
    };

    const onPhotoSelect = (e: ChangeEvent<HTMLInputElement>): void => {
        const uploadedFiles = e.target.files;
        if (uploadedFiles) {
            const totalMediaLength = media.length + uploadedFiles.length;
            if (totalMediaLength > MAX_MEDIA_FILES_NUMBER) {
                const message = intl.formatMessage({
                    id: 'turn-to.review-modal.max-number-of-files',
                    defaultMessage:
                        'Cannot upload more than 5 files. Please select fewer and try again.',
                });
                form.setError('photo', { type: 'maxMediaLength', message });
                return;
            }
            onOpenPhotoModal();
            setFiles(uploadedFiles);
            onCloseWriteReview();
            onCloseWarningModal();
        }
    };

    const onCloseModals = (): void => {
        form.reset();
        setFiles(null);
        setMedia([]);
        onCloseUserDataModal();
        onCloseWriteReview();
        onCloseWarningModal();
    };

    const submitReview = async (): Promise<void> => {
        performFilesValidation(media as Photo[]);
        await form.trigger('firstName');
        await form.trigger('lastName');
        await form.trigger('email');

        const errorsLength = Object.keys(form.formState.errors).length;
        if (errorsLength) {
            return;
        }

        const review = {
            rating: form.getValues('rating'),
            text: form.getValues('review'),
            title: form.getValues('reviewTitle'),
            user: {
                firstName: form.getValues('firstName'),
                lastName: form.getValues('lastName'),
                nickName: form.getValues('nickName'),
                emailAddress: form.getValues('email'),
            },
            media: {
                photo: media
                    .filter((mediaFile) => mediaFile.type === 'photo')
                    .map((mediaFile) => ({
                        b64data: (mediaFile as Photo).b64data,
                        caption: (mediaFile as Photo).caption,
                    })),
                video: media.filter((mediaFile) => mediaFile.type === 'video'),
            },
            catalogItems: [
                {
                    sku: product?.master?.masterId,
                },
            ],
        };

        await postReview
            .mutateAsync(review as CreateReviewType)
            .then(() => onCloseModals())
            .catch((error: HTTPError) => {
                if (error.status === 400 && error.message === 'Bad Request') {
                    form.setError('email', {
                        message: intl.formatMessage({
                            defaultMessage: 'User already submitted review for this SKU',
                            id: 'turn-to.submit-modal.review-already-exists',
                        }),
                    });
                }
            });
    };

    const removeMedia = (mediaToRemove: Photo | Video): void => {
        setMedia(media.filter((photo) => photo.id !== mediaToRemove.id));
        form.clearErrors('photo');
    };

    const onVideoModalSubmit = async (video: Video): Promise<void> => {
        await form.trigger('video');

        const errorsLength = Object.keys(form.formState.errors).length;
        if (errorsLength) {
            return;
        }
        setMedia((prevMedia) => [...prevMedia, video]);

        onOpenWriteReview();
        onCloseVideoModal();
    };

    const proceedToUserData = async (): Promise<void> => {
        await form.trigger('review');
        await form.trigger('reviewTitle');
        await form.trigger('rating');
        const errorsLength = Object.keys(form.formState.errors).length;
        if (errorsLength) {
            return;
        }
        onOpenUserDataModal();
        onCloseWriteReview();
    };

    const openVideoModal = (): void => {
        onOpenVideoModal();
        onCloseWriteReview();
    };

    return (
        <>
            {isWriteReviewOpen && (
                <WriteReviewModal
                    onClose={handleCloseFlow}
                    isOpen={isWriteReviewOpen}
                    form={form}
                    fields={fields}
                    onOpenVideoModal={openVideoModal}
                    onProceedToUserData={proceedToUserData}
                    removeMedia={removeMedia}
                    onPhotoSelect={onPhotoSelect}
                    media={media}
                    isFileSizeLimitExceeded={isFilesLimitExceeded}
                />
            )}
            {isPhotoModalOpen && (
                <PhotoModal
                    isOpen={isPhotoModalOpen}
                    onClose={() => returnToWriteReviewModal(onClosePhotoModal)}
                    files={files}
                    onSubmit={onSubmitPhotoModal}
                />
            )}
            {isVideoModalOpen && (
                <VideoModal
                    isOpen={isVideoModalOpen}
                    onClose={() => returnToWriteReviewModal(onCloseVideoModal)}
                    productName={product?.name}
                    productImage={product?.imageGroups?.[0].images[0].link}
                    field={fields.video}
                    onSubmit={onVideoModalSubmit}
                    form={form}
                />
            )}
            {isUserDataModalOpen && (
                <SubmitQuestionModal
                    id="create-review"
                    isOpen={isUserDataModalOpen}
                    onClose={handleCloseFlow}
                    fields={fields}
                    onSubmit={submitReview}
                    title={intl.formatMessage({
                        id: 'turn-to.submit-modal.find-your-profile',
                        defaultMessage: 'Find your profile',
                    })}
                    headerText={
                        <FormattedMessage
                            id="turn-to.submit-modal.create-review-description"
                            defaultMessage="We need to know who you are before we post your review. Please provide your name and email address."
                        />
                    }
                    variant="turn-to"
                />
            )}
            {isWarningModalOpen && (
                <WarningModal
                    isOpen={isWarningModalOpen}
                    onReturn={onReturnToWriteReview}
                    onConfirmClose={onCloseModals}
                />
            )}
        </>
    );
};
